import {
  Controller, Get, Post, Patch, Param, Body, UseGuards, Req, ForbiddenException
} from '@nestjs/common';
import { ProposalsService } from './proposals.service';
import {
  CreateProposalsDto, UpdateProposalsDto,
  CreateProposalAttachmentsDto, UpdateProposalAttachmentsDto,
  CreateProposalItemsDto, UpdateProposalItemsDto,
  CreateProposalPricingSummaryDto, UpdateProposalPricingSummaryDto,
} from '@crm/dto';
import { ApiResponseInterface, ProposalStatus, UserRole } from '@crm/interface';
import { AuthGuard } from '../guards/auth.guard';
import { RolesGuard } from '../guards/roles.guard';
import { Roles } from '../guards/roles.decorator';
import { IsEnum, IsOptional, IsString, IsUUID } from 'class-validator';

class ApproveProposalDto {
  @IsUUID()
  id!: string;

  @IsEnum(ProposalStatus)
  status!: ProposalStatus;

  @IsOptional()
  @IsString()
  updated_by_id?: string;
}

@Controller('proposals')
export class ProposalsController {
  constructor(private readonly proposalsService: ProposalsService) {}

  // ====== PROPOSALS ======
  @Get()
  async getAll(): Promise<ApiResponseInterface> {
    return this.proposalsService.getAll();
  }

  @Get('supervisor')
  async getForSupervisor(): Promise<ApiResponseInterface> {
    return this.proposalsService.getForSupervisor();
  }

  @Get('user/:userId')
  async getByUserId(@Param('userId') userId: string): Promise<ApiResponseInterface> {
    return this.proposalsService.getByUserId(userId);
  }

  @Get(':id')
  async getById(@Param('id') id: string): Promise<ApiResponseInterface> {
    return this.proposalsService.getById(id);
  }

  @Post()
  async create(@Body() dto: CreateProposalsDto): Promise<ApiResponseInterface> {
    return this.proposalsService.create(dto);
  }

  @Patch()
  async update(@Body() dto: UpdateProposalsDto): Promise<ApiResponseInterface> {
    return this.proposalsService.update(dto);
  }

  @Patch('soft-delete')
  async softDelete(@Body() dto: UpdateProposalsDto): Promise<ApiResponseInterface> {
    return this.proposalsService.softDelete(dto);
  }

  @Patch('save-draft')
  async saveAsDraft(@Body() dto: UpdateProposalsDto): Promise<ApiResponseInterface> {
    return this.proposalsService.saveAsDraft(dto);
  }

  // ====== APPROVE PROPOSAL (management + supervisor; management required for >250k) ======
  @Patch('approve')
  @UseGuards(AuthGuard, RolesGuard)
  @Roles(UserRole.MANAGEMENT, UserRole.SUPERVISOR)
  async approveProposal(
    @Body() dto: ApproveProposalDto,
    @Req() req: any,
  ): Promise<ApiResponseInterface> {
    return this.proposalsService.approveProposal(dto.id, dto.status, req.user.role, dto.updated_by_id);
  }

  // ====== PROPOSAL ATTACHMENTS ======
  @Post('attachments')
  async createAttachment(@Body() dto: CreateProposalAttachmentsDto): Promise<ApiResponseInterface> {
    return this.proposalsService.createProposalAttachment(dto);
  }

  @Patch('attachments')
  async updateAttachment(@Body() dto: UpdateProposalAttachmentsDto): Promise<ApiResponseInterface> {
    return this.proposalsService.updateProposalAttachment(dto);
  }

  // ====== PROPOSAL ITEMS ======
  @Get(':proposalId/items')
  async getItemsByProposalId(@Param('proposalId') proposalId: string): Promise<ApiResponseInterface> {
    return this.proposalsService.getProposalItemByProposalId(proposalId);
  }

  @Post('items')
  async createItem(@Body() dto: CreateProposalItemsDto): Promise<ApiResponseInterface> {
    return this.proposalsService.createProposalItem(dto);
  }

  @Patch('items')
  async updateItem(@Body() dto: UpdateProposalItemsDto): Promise<ApiResponseInterface> {
    return this.proposalsService.updateProposalItem(dto);
  }

  @Patch('items/soft-delete')
  async softDeleteItem(@Body() dto: UpdateProposalItemsDto): Promise<ApiResponseInterface> {
    return this.proposalsService.softDeleteProposalItem(dto);
  }

  // ====== PROPOSAL PRICING SUMMARY ======
  @Get(':proposalId/summary')
  async getSummaryByProposalId(@Param('proposalId') proposalId: string): Promise<ApiResponseInterface> {
    return this.proposalsService.getProposalSummaryByProposalId(proposalId);
  }

  @Post('summary')
  async createSummary(@Body() dto: CreateProposalPricingSummaryDto): Promise<ApiResponseInterface> {
    return this.proposalsService.createProposalSummary(dto);
  }

  @Patch('summary')
  async updateSummary(@Body() dto: UpdateProposalPricingSummaryDto): Promise<ApiResponseInterface> {
    return this.proposalsService.updateProposalSummary(dto);
  }
}
